import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Banknote, CheckCircle2, CircleDollarSign, ShieldCheck } from "lucide-react";
import { Suspense } from "react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";

import { FinanceListSkeleton, FinanceMetricsSkeleton } from "./area-skeleton";
import { MetricCard, StatePill } from "./primitives";
import { FinanceTimeline, FinanceTimelineCard } from "./timeline";
import type { FinanceModal } from "./types";

const RECEIPT_STATE = {
	confirmed: { label: "Confirmado", tone: "green" },
	pending: { label: "Falta comprovante", tone: "amber" },
	reversed: { label: "Estornado", tone: "coral" },
	cancelled: { label: "Cancelado", tone: "muted" },
} as const;

interface ReceiptsAreaProps {
	month: string;
	page: number;
	onPage: (page: number) => void;
	onModal: (modal: FinanceModal) => void;
}

export function ReceiptsArea(props: ReceiptsAreaProps) {
	return (
		<>
			<ErrorBoundary key={props.month}>
				<Suspense fallback={<FinanceMetricsSkeleton />}>
					<ReceiptsSummary month={props.month} />
				</Suspense>
			</ErrorBoundary>
			<ErrorBoundary key={`${props.month}:${props.page}`}>
				<Suspense fallback={<FinanceListSkeleton />}>
					<ReceiptsList {...props} />
				</Suspense>
			</ErrorBoundary>
		</>
	);
}

function ReceiptsSummary({ month }: { month: string }) {
	const { data } = useSuspenseQuery(
		orpc.financeiro.receipts.summary.queryOptions({ input: { month } }),
	);

	return (
		<div className="mt-5 grid gap-3 sm:grid-cols-3">
			<MetricCard
				tone="hero"
				icon={CircleDollarSign}
				label="Recebido"
				value={FinanceiroLabels.money(data.sum_cents)}
				detail={`${data.total} registros no mês`}
			/>
			<MetricCard
				icon={ShieldCheck}
				label="Confirmado"
				value={FinanceiroLabels.money(data.confirmed_cents)}
				detail={`${data.confirmed_count} com comprovante`}
			/>
			<MetricCard
				icon={CheckCircle2}
				label="A conferir"
				value={`${Math.max(0, data.total - data.confirmed_count)}`}
				detail="inclui cancelados e estornos"
			/>
		</div>
	);
}

function ReceiptsList({ month, page, onPage, onModal }: ReceiptsAreaProps) {
	const queryClient = useQueryClient();
	const { data } = useSuspenseQuery(
		orpc.financeiro.receipts.list.queryOptions({ input: { month, page } }),
	);
	const cancel = useMutation(
		orpc.financeiro.receipts.cancel.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: orpc.financeiro.key(),
				});
				toast.success("Recebimento cancelado");
			},
			onError: () => toast.error("Não foi possível cancelar o recebimento"),
		}),
	);

	return (
		<>
			{data.data.length === 0 && (
				<div className="surface-panel mt-5">
					<EmptyState
						icon={Banknote}
						title="Nenhum recebimento neste mês"
						description="Os pagamentos registrados pela agenda e por aqui aparecem nesta área."
					/>
				</div>
			)}
			{data.data.length > 0 && (
				<FinanceTimeline
					items={data.data}
					dateFor={(receipt) => receipt.received_on}
					itemKey={(receipt) => receipt.id}
					renderItem={(receipt) => {
						const state = RECEIPT_STATE[receipt.state];
						const returnsEvidence = receipt.state === "confirmed" && receipt.method !== "cash";
						const automaticEvidence =
							(receipt.bank_transaction_id !== null || receipt.card_sale_id !== null) &&
							receipt.evidence_linked_by?.endsWith(":auto") === true;

						return (
							<FinanceTimelineCard
								icon={Banknote}
								title={receipt.person_name}
								detail={FinanceiroLabels.receipt.method[receipt.method]}
								note={receipt.description}
								amount={FinanceiroLabels.money(receipt.amount_cents)}
								status={
									<div className="flex flex-wrap justify-end gap-1">
										<StatePill label={state.label} tone={state.tone} />
										{automaticEvidence && <StatePill label="Vínculo automático" tone="muted" />}
									</div>
								}
								actions={
									receipt.state !== "cancelled" && (
										<>
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													onModal({
														kind: "receipt",
														initial: {
															replaces_id: receipt.id,
															person:
																receipt.person_link === "patient_confirmed"
																	? {
																			kind: "patient",
																			patientId: receipt.person_id,
																			sendpulseId: receipt.person_sendpulse_id,
																			name: receipt.person_name,
																		}
																	: {
																			kind: "lead",
																			leadId: receipt.person_id,
																			sendpulseId: receipt.person_sendpulse_id,
																			name: receipt.person_name,
																		},
															attendance_id: receipt.attendance_id,
															method: receipt.method,
															amount_cents: receipt.amount_cents,
															received_on: receipt.received_on,
															declared_installment_count: receipt.declared_installment_count,
															description: receipt.description,
														},
													})
												}
											>
												Corrigir
											</Button>
											{receipt.method !== "cash" && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() =>
														onModal({
															kind: "evidence",
															target: { kind: "receipt", id: receipt.id },
														})
													}
												>
													{receipt.state === "confirmed" && "Trocar comprovante"}
													{receipt.state !== "confirmed" && "Vincular"}
												</Button>
											)}
											{returnsEvidence && (
												<ReceiptUnlinkEvidence id={receipt.id} automatic={automaticEvidence} />
											)}
											<ConfirmationDialog
												slot="cancel-receipt"
												tone="destructive"
												title="Cancelar recebimento?"
												description={`Cancelar ${FinanceiroLabels.money(receipt.amount_cents)} de ${receipt.person_name}, registrado em ${format.date(receipt.received_on)}? O registro continuará no histórico${returnsEvidence ? " e o comprovante voltará para a conciliação" : ""}.`}
												pendingLabel="Cancelando"
												isPending={cancel.isPending}
												onConfirm={() => cancel.mutate({ id: receipt.id })}
												className="px-2 text-xs text-muted-foreground hover:text-destructive"
											>
												Cancelar
											</ConfirmationDialog>
										</>
									)
								}
							/>
						);
					}}
					page={page}
					pages={data.pages}
					onPage={onPage}
				/>
			)}
		</>
	);
}

function ReceiptUnlinkEvidence({ id, automatic }: { id: number; automatic: boolean }) {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.financeiro.receipts.unlinkEvidence.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success("Comprovante desvinculado");
			},
			onError: () => toast.error("Não foi possível desvincular o comprovante"),
		}),
	);

	return (
		<ConfirmationDialog
			slot="unlink-receipt-evidence"
			tone="neutral"
			title="Desvincular comprovante?"
			description={
				automatic
					? "O vínculo automático será removido e o recebimento voltará para a conciliação."
					: "O recebimento voltará para a conciliação."
			}
			confirmLabel="Desvincular"
			pendingLabel="Desvinculando"
			isPending={isPending}
			onConfirm={() => mutate({ id })}
			className="px-2 text-xs text-muted-foreground hover:text-foreground"
		>
			Desvincular
		</ConfirmationDialog>
	);
}
